import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    TextareaAutosize, Container, Typography, Paper, Box, AlertColor,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

interface FormData {
    name: string;
    email: string;
    service: string;
    description: string;
}

interface QuoteFormState {
    formData: FormData;
    snackbarOpen: boolean;
    alertType: AlertColor | undefined;
    isValid: boolean;
}

interface QuoteFormProps extends WithTranslation {}

class QuoteForm extends React.Component<QuoteFormProps, QuoteFormState> {
    constructor(props: QuoteFormProps) {
        super(props);
        this.state = {
            formData: {
                name: '',
                email: '',
                service: '',
                description: '',
            },
            snackbarOpen: false,
            alertType: 'success',
            isValid: false
        };
    }

    private handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ): void => {
        const { name, value } = e.target;
        this.setState((prevState) => ({
            formData: {
                ...prevState.formData,
                [name]: value,
            },
            isValid: name === 'service' || name === 'name' || name === 'email' || name === 'description' ? value !== "" : prevState.isValid
        }));
    };

    private handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string): void => {
        if (reason === 'clickaway') {
            return;
        }

        this.setState({ snackbarOpen: false });
    };

    private handleSnackbarAndAlertType(alertType: AlertColor | undefined): void {
        this.setState({
            snackbarOpen: true,
            alertType: alertType
        })
    }

    private handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        const { formData } = this.state;

        const url: string | undefined = process.env.REACT_APP_FORMSPREE_URL;

        if (!this.state.isValid){
            this.handleSnackbarAndAlertType('warning')
            return;
        }

        try {
            // @ts-ignore
            const response: Response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                this.setState({
                    formData: {
                        name: '',
                        email: '',
                        service: '',
                        description: '',
                    },
                    snackbarOpen: true,
                    alertType: 'success',
                });
            } else {
                this.handleSnackbarAndAlertType('error')
            }
        } catch (error) {
            console.error('Ouh error: ', error);
        }
    };


    private changeLanguage = (language: string): void => {
        this.props.i18n.changeLanguage(language);
    };

    render() {
        const { formData } = this.state;
        const { t } = this.props;

        return (
            <>
                <Snackbar
                    open={this.state.snackbarOpen}
                    autoHideDuration={6000}
                    onClose={this.handleSnackbarClose}
                >
                    <Alert
                        onClose={this.handleSnackbarClose}
                        severity={this.state.alertType}
                        sx={{ width: '100%' }}
                    >
                        {
                            this.state.alertType === 'success' ? t('formHasBeenSent') :
                                this.state.alertType === 'warning' ? t('formFillInAllFields') :
                                    t('formSendingError')
                        }
                    </Alert>
                </Snackbar>
            <Box style={{padding: '60px 0', backgroundColor: "#fff"}} id="contact">
                <Container>
                    <Typography variant="h3" gutterBottom style={{ fontWeight: 'bold', color: '#0A74DA', textAlign: 'center' }}>
                        {t('requestForQuotation')}
                    </Typography>
                    <Paper elevation={3} style={{ padding: '20px' }}>
                        <form onSubmit={this.handleSubmit}>
                            <Box mb={2}>
                                <TextField
                                    label={t('fullName')}
                                    name="name"
                                    value={formData.name}
                                    onChange={this.handleChange}
                                    fullWidth
                                />
                            </Box>

                            <Box mb={2}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={this.handleChange}
                                    fullWidth
                                />
                            </Box>

                            <Box mb={2}>
                                <FormControl fullWidth>
                                    <Select
                                        name="service"
                                        value={formData.service}
                                        displayEmpty
                                        // @ts-ignore
                                        onChange={this.handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>{t('selectService')}</em>
                                        </MenuItem>
                                        <MenuItem value="web-development">{t('webDevelopment')}</MenuItem>
                                        <MenuItem value="e-commerce">{t('eCommerce')}</MenuItem>
                                        <MenuItem value="cooperate">{t('cooperate')}</MenuItem>
                                        <MenuItem value="business">{t('business')}</MenuItem>
                                        <MenuItem value="other">{t('other')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box mb={2}>
                                <TextareaAutosize
                                    name="description"
                                    value={formData.description}
                                    onChange={this.handleChange}
                                    placeholder={t('content')}
                                    style={{ width: '100%', padding: '10px', minHeight: '150px' }}
                                />
                            </Box>

                            <Box textAlign="center" mt={2}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                >
                                    {t('sendInquiry')}
                                </Button>
                            </Box>
                        </form>
                    </Paper>
                </Container>
            </Box>
            </>
        );
    }
}

export default withTranslation()(QuoteForm);
